/* Neuro Reset page: header background past the top, and the hero film. */
(() => {
  const site = document.querySelector('.nr-site');
  const onScroll = () => site?.classList.toggle('is-scrolled', scrollY > 40);
  addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  // Started from here rather than with autoplay, so reduced motion and data
  // saver keep the photo. The photo also stays if the browser refuses to play.
  const film = document.querySelector('.nr-hero__video');
  const still = matchMedia('(prefers-reduced-motion: reduce)').matches || navigator.connection?.saveData;
  if (film && !still) {
    film.muted = true;
    film.defaultPlaybackRate = Number(film.closest('[data-film-speed]').dataset.filmSpeed) || 1;
    film.playbackRate = film.defaultPlaybackRate;
    film.addEventListener('playing', () => film.classList.add('is-playing'), { once: true });
    film.play().catch(() => {});
  }

  // Carousel: rotates while on screen, until someone uses it. Arrows, swipes and
  // keyboard focus stop it for good. A hover does not: it fills the screen, so a
  // resting cursor is nearly always over it and it would never move.
  const startCarousel = (carousel) => {
    const slides = [...carousel.querySelectorAll('.nr-carousel__slide')];
    if (slides.length < 2) return;
    let active = 0;
    let timer = null;
    let stopped = false;
    let onScreen = false;
    const seconds = Number(carousel.dataset.seconds);
    const auto = seconds > 0 && !matchMedia('(prefers-reduced-motion: reduce)').matches;

    const show = (index) => {
      active = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === active);
        slide.setAttribute('aria-hidden', i !== active);
      });
    };
    const sync = () => {
      const run = auto && !stopped && onScreen && !document.hidden;
      if (run && !timer) timer = setInterval(() => show(active + 1), seconds * 1000);
      if (!run && timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const take = () => {
      stopped = true;
      sync();
    };

    carousel.addEventListener('click', (event) => {
      const arrow = event.target.closest('[data-nr-step]');
      if (!arrow) return;
      take();
      show(active + Number(arrow.dataset.nrStep));
    });
    carousel.addEventListener('focusin', take);
    carousel.addEventListener('keydown', (event) => {
      const step = { ArrowLeft: -1, ArrowRight: 1 }[event.key];
      if (!step) return;
      take();
      show(active + step);
    });
    let touchX = null;
    carousel.addEventListener('touchstart', (event) => {
      touchX = event.touches[0].clientX;
    }, { passive: true });
    carousel.addEventListener('touchend', (event) => {
      if (touchX === null) return;
      const moved = event.changedTouches[0].clientX - touchX;
      touchX = null;
      if (Math.abs(moved) < 40) return;
      take();
      show(active + (moved < 0 ? 1 : -1));
    }, { passive: true });
    document.addEventListener('visibilitychange', sync);
    new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      sync();
    }, { threshold: 0.4 }).observe(carousel);
  };
  document.querySelectorAll('[data-nr-carousel]').forEach(startCarousel);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-nr-carousel]').forEach(startCarousel);
  });

  // Pinned rows: the wrapper's scroll position picks two neighbouring rows and
  // hands over between them in three staggered steps, so text never sits on
  // text: old copy out, pictures crossfade, new copy in. Each row rests for the
  // first part of its screen so it can be read, and the position eases rather
  // than jumping with each wheel notch.
  const hold = 0.3;
  const clamp = (t) => Math.min(Math.max(t, 0), 1);
  const phase = (t, from, to) => {
    const x = clamp((t - from) / (to - from));
    return x * x * (3 - 2 * x);
  };
  document.querySelectorAll('[data-nr-pin]').forEach((wrap) => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const stage = wrap.querySelector('.nr-rows__stage');
    const rows = [...wrap.querySelectorAll('.nr-row')];
    if (rows.length < 2) return;
    // A short window, including a page zoomed to 200-400%, cannot hold a row
    // inside a fixed stage without clipping it, so the rows stack instead.
    const tallEnough = matchMedia('(min-height: 600px)');
    const pinned = () => wrap.classList.contains('is-pinned');

    const target = () => {
      const travel = wrap.offsetHeight - stage.offsetHeight;
      const scrolled = Math.min(Math.max(-wrap.getBoundingClientRect().top, 0), travel);
      return (scrolled / travel) * (rows.length - 1);
    };

    // Rows are faded rather than hidden, so screen readers can reach all of
    // them whichever one is on screen.
    const paint = (position) => {
      const index = Math.min(Math.floor(position), rows.length - 2);
      const t = clamp((position - index - hold) / (1 - hold));
      const copyOut = phase(t, 0, 0.4);
      const image = phase(t, 0.15, 0.85);
      const copyIn = phase(t, 0.6, 1);
      rows.forEach((row, i) => {
        const outgoing = i === index;
        const incoming = i === index + 1;
        const shown = outgoing || incoming;
        row.style.pointerEvents = (incoming ? copyIn > 0.5 : outgoing && copyIn <= 0.5) ? 'auto' : 'none';
        row.style.setProperty('--nr-image', !shown ? 0 : incoming ? image : 1 - image);
        row.style.setProperty('--nr-copy', !shown ? 0 : incoming ? copyIn : 1 - copyOut);
        row.style.setProperty('--nr-zoom', incoming ? 1.06 - 0.06 * image : 1 + 0.03 * image);
        row.style.setProperty('--nr-lift', `${incoming ? 32 * (1 - copyIn) : -24 * copyOut}px`);
      });
      stage.style.setProperty('--nr-progress', position / (rows.length - 1));
    };

    let current = 0;
    let running = false;
    const step = () => {
      if (!pinned()) {
        running = false;
        return;
      }
      const goal = target();
      current += (goal - current) * 0.14;
      if (Math.abs(goal - current) < 0.0008) current = goal;
      paint(current);
      running = current !== goal;
      if (running) requestAnimationFrame(step);
    };
    const wake = () => {
      if (running || !pinned()) return;
      running = true;
      requestAnimationFrame(step);
    };
    const fit = () => {
      wrap.classList.toggle('is-pinned', tallEnough.matches);
      if (!tallEnough.matches) {
        rows.forEach((row) => { row.style.pointerEvents = ''; });
        return;
      }
      current = target();
      paint(current);
    };
    addEventListener('scroll', wake, { passive: true });
    addEventListener('resize', wake, { passive: true });
    tallEnough.addEventListener('change', fit);
    // Tabbing to a link in a faded row brings that row on screen.
    wrap.addEventListener('focusin', (event) => {
      const index = rows.indexOf(event.target.closest('.nr-row'));
      if (index < 0 || !pinned()) return;
      const top = wrap.getBoundingClientRect().top + scrollY;
      const travel = wrap.offsetHeight - stage.offsetHeight;
      scrollTo({ top: top + (travel * index) / (rows.length - 1), behavior: 'instant' });
    });
    fit();
  });
})();
